import { observer } from 'mobx-react-lite';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { useInjection } from 'inversify-react';
import { useTranslation } from '@shared/utils/i18n';
import { ViewModel } from '../model/ViewModel';

// OSI-823: общий стиль абзацев подсказки — многострочные тексты приходят из i18n с \n
const paragraphSx = { whiteSpace: 'pre-line', mb: 2 } as const;
const subtitleSx = { fontWeight: 600, mb: 1 } as const;

// OSI-823: кнопка «Как считается пеня» справа от чекбокса «Включить начисление пени» + модалка с пояснением.
// Текст зависит от схемы выставления начислений дома (Osi.PrintMonth): 0 — «месяц в месяц», -1 — «за предыдущий месяц».
// Для остальных значений (и пока osiInfo не загружен) кнопка не рендерится.
export const PenaltiesHelpButton: React.FC = observer(() => {
  const { t } = useTranslation();
  const vm = useInjection<ViewModel>(ViewModel);

  const { mode } = vm;
  if (!mode) return null;

  const isCurrentMonth = mode === 'currentMonth';

  const renderContent = () => {
    // OSI-823: пока грузится день начисления — спиннер
    if (vm.isLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      );
    }

    // OSI-823: текст с пустым/нулевым днём не показываем — только сообщение об ошибке
    if (!vm.hasAccrualDay) {
      return (
        <Typography variant="body2" color="error">
          {vm.loadError ?? t('common:error')}
        </Typography>
      );
    }

    // OSI-823: порядок: intro → формула → ставка → основной долг → пример → продолжение примера → итог.
    // Варианты отличаются только intro и первой строкой примера.
    return (
      <Box>
        <Typography variant="body2" sx={paragraphSx}>
          {t(isCurrentMonth ? 'accruals:penaltiesHelp.introCurrentMonth' : 'accruals:penaltiesHelp.introPrevMonth', {
            day: vm.accrualDay
          })}
        </Typography>

        <Typography variant="subtitle2" sx={subtitleSx}>
          {t('accruals:penaltiesHelp.formulaTitle')}
        </Typography>
        <Typography variant="body2" sx={{ ...paragraphSx, fontWeight: 500 }}>
          {t('accruals:penaltiesHelp.formula')}
        </Typography>
        <Typography variant="body2" sx={paragraphSx}>
          {t('accruals:penaltiesHelp.rateNote')}
        </Typography>
        <Typography variant="body2" sx={paragraphSx}>
          {t('accruals:penaltiesHelp.principalNote')}
        </Typography>

        <Typography variant="subtitle2" sx={subtitleSx}>
          {t('accruals:penaltiesHelp.exampleTitle')}
        </Typography>
        <Typography variant="body2" sx={paragraphSx}>
          {t(isCurrentMonth ? 'accruals:penaltiesHelp.exampleCurrentMonth' : 'accruals:penaltiesHelp.examplePrevMonth')}
        </Typography>
        <Typography variant="body2" sx={paragraphSx}>
          {t('accruals:penaltiesHelp.exampleContinue')}
        </Typography>
        <Typography variant="body2" sx={{ ...paragraphSx, mb: 0 }}>
          {t('accruals:penaltiesHelp.total')}
        </Typography>
      </Box>
    );
  };

  return (
    <Box>
      <Button variant="outlined" size="small" startIcon={<HelpOutlineIcon />} onClick={vm.openModal}>
        {t('accruals:penaltiesHelp.button')}
      </Button>
      <Dialog open={vm.isOpenModal} onClose={vm.closeModal} maxWidth="sm" fullWidth>
        <DialogTitle>{t('accruals:penaltiesHelp.title')}</DialogTitle>
        <DialogContent>{renderContent()}</DialogContent>
        <DialogActions>
          <Button onClick={vm.closeModal}>{t('common:close')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});
